import { type FormikProps } from "formik";
import React from "react";

import { ValidatedForm } from "../../../../../components/forms";

import { type FileLike, type Form } from "interfaces";
import { extractFormikProps } from "utils/formikUtils";
import { nameofFactory } from "utils/nameof";

import ImagePreview from "components/media/imagePreview/ImagePreview";
import {
  IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS,
  IMAGE_PREVIEW_PLACEHOLDER_VARIANTS,
} from "components/media/imagePreview/placeholders";
import { type EntityMode, type FormFields } from "../../types/models";
import { EndpointsToGetSasLink } from "../../../../../enums";
import UploadField from "./UploadField";
import { uploadThumbnailAction } from "../../state/thunks/DetailsThunk";
import { useRtn } from "../../../../../hooks/useRtn";
import { ScormProcessManifestSuccess } from "../../../../Application/services/realTimeNotification/events/library/libraryEvents";
import { ViewContentLinkButton } from "../../../../../components";
import { getContentLinkToEupFromPreview } from "../../../../../utils/eupUtils";

export type FormProps = FormikProps<FormFields> &
  Form & {
    id: number;
    isReadOnlyView: boolean;
    isEntityCreated: boolean;
    canBePublished: boolean;
    processingError?: string;
    mode: EntityMode;
    saveData: (values?: FormFields) => void;
  };

const ConfigurationForm = (props: FormProps) => {
  const { values } = props;
  const formik = extractFormikProps<FormFields, FormProps>(props);
  const nameof = nameofFactory<FormFields>();

  const onTitleBlur = (propertyName: string) => {
    const descriptionField = nameof("description");
    if (!props.isEntityCreated && !props.touched[descriptionField]) {
      props.setFieldValue(descriptionField, values.title, true);
    }

    props.onBlur(propertyName);
  };

  const onFileUpload = (fileName: string, uploadedFiles: FileLike[], title: string, description: string) => {
    const newValues = { ...values, fileName, title, description, uploadedFiles };
    props.setValues(newValues);
    props.saveData(newValues);
  };

  useRtn(ScormProcessManifestSuccess, (payload: { title: string }) => {
    formik.setValues({ ...values, title: payload.title, description: payload.title });
  });

  const handleThumbnailChanged = (_: string, fileKey: string) => {
    uploadThumbnailAction(props.id, fileKey);
    props.saveData();
  };
  const renderFields = (): React.ReactNode => {
    return (
      <>
        {props.hasBeenPublished && (
          <ViewContentLinkButton href={getContentLinkToEupFromPreview["SCORM"](props.id)} fitted />
        )}
        <UploadField
          id={props.id}
          mode={props.mode}
          disabled={!!props.disabled}
          value={values.uploadedFiles.length > 0 ? values.uploadedFiles : [{ name: values.fileName } as FileLike]}
          formik={formik}
          firstUpload={props.id <= 0}
          onFileUpload={onFileUpload}
          isReadOnlyView={props.isReadOnlyView}
          processingError={props.processingError}
          isProcessing={props.isEntityCreated && !props.canBePublished && !props.processingError}
        />
        {props.isEntityCreated && (
          <>
            <ValidatedForm.InputField
              {...formik}
              label="Package Name"
              placeholder="Enter Package Name"
              value={values.title}
              propertyName={nameof("title")}
              onBlur={onTitleBlur}
              markAsRequired
              disabled={props.disabled || !props.canBePublished}
              isReadOnlyView={props.isReadOnlyView}
            />

            <ValidatedForm.TextAreaField
              {...formik}
              label="Description"
              placeholder="Enter Description"
              value={values.description}
              propertyName={nameof("description")}
              onBlur={props.onBlur}
              disabled={props.disabled || !props.canBePublished}
              markAsRequired
              isReadOnlyView={props.isReadOnlyView}
            />

            <ImagePreview
              {...formik}
              combinedIcon
              customImagePreview
              label="Thumbnail Image"
              disabled={props.disabled}
              value={values.thumbnailUrl}
              isReadOnlyView={props.isReadOnlyView}
              propertyName={nameof("thumbnailUrl")}
              endpointToGetSasLink={EndpointsToGetSasLink.Scorm}
              placeholder={IMAGE_PREVIEW_PLACEHOLDER_VARIANTS.image}
              headerPlaceholder={IMAGE_PREVIEW_HEADER_PLACEHOLDER_VARIANTS.image}
              onImageChanged={handleThumbnailChanged}
            />
          </>
        )}
      </>
    );
  };

  const { isLoading, disablePreventTransitionPrompt } = props;
  return (
    <ValidatedForm
      dirty={props.dirty}
      unsavedChangesPrompt={{
        title: "Exit Without Saving?",
        message:
          "Are you sure you want to exit without saving this SCORM Package? All information entered will be lost.",
      }}
      disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      isLoading={isLoading}
      formWidthComputer={6}
      withoutFormik
    >
      {renderFields}
    </ValidatedForm>
  );
};

export default ConfigurationForm;
